import { useCallback, useEffect, useState } from 'react'
import { Accordion, Alert, Button, Text, Checkbox, Icon } from '@/ui'
import { TypographyStyles } from '@/styles'
import { UploadDocumentType } from '@/constants/businessConstants'
import { faUpload } from '@fortawesome/pro-regular-svg-icons'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { UnsupportedInstitutionCard } from './UnsupportedInstitutionCard'
import { EditableStatementTable } from '../EditableStatementTable'
import { useReselector } from '@/utils/sharedHooks'
import { selectUserStatements } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { useSearchParams } from 'react-router-dom'
import { useAnalyticsTrack } from '@/features/amplitude'

// TODO: migrate to v2
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'

export const NoLBAInstitutionCard = ({
  filteredPlaidItems,
}: {
  filteredPlaidItems: PlaidItem[]
}) => {
  const dispatch = useAppDispatch()
  const defaultSkipState =
    localStorage.getItem('skipUploadStatements') === 'true' || false
  const [uploadFileModalOpen, setUploadFileModalOpen] = useState(false)
  const [checked, setChecked] = useState(defaultSkipState)
  const [searchParams, setSearchParams] = useSearchParams()
  const track = useAnalyticsTrack()

  const currentYear = new Date().getFullYear()
  const statements = useReselector(selectUserStatements)

  const setSkipQueryParam = useCallback(() => {
    searchParams.set('skip', checked.toString())
    setSearchParams(searchParams)
  }, [searchParams, setSearchParams, checked])

  useEffect(() => {
    setSkipQueryParam()
  }, [checked, setSkipQueryParam])

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchFinancialAccountsIfNeeded())
      await dispatch(fetchPlaidItemsIfNeeded())
    }

    fetch()
  }, [dispatch])

  const handleSkip = () => {
    setChecked((prevChecked) => !prevChecked)
    localStorage.setItem('skipUploadStatements', (!checked).toString())
  }

  useEffect(() => {
    if (checked) {
      track(
        'checked I just created my business bank account and dont have any statements yet'
      )
    }
  }, [checked, track])

  return (
    <div className="space-y-6">
      <Text variant={TypographyStyles.H2}>Upload monthly bank statements</Text>

      <Text>
        Finish setting up your account by uploading the monthly bank statements
        below. This is important for compliance and completeness of your books.
      </Text>

      {filteredPlaidItems.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {filteredPlaidItems.map((item) => (
            <UnsupportedInstitutionCard key={item.id} institution={item} />
          ))}
        </div>
      )}

      <Text variant={TypographyStyles.H3}>Instructions</Text>
      <div className="space-y-1">
        <Text>1. Log into each account listed above.</Text>
        <Text>
          2. Download all monthly statements for each month of {currentYear}.
        </Text>
        <Text>3. Upload these statements (JPG, PNG, PDF) here.</Text>
      </div>

      <Accordion type="multiple" variant="text">
        <Accordion.Item value="why-statements">
          <Accordion.Trigger>
            Why do you need my bank statements?
          </Accordion.Trigger>
          <Accordion.Content>
            <Text>
              These help us complete your financial record from before you
              joined Heard. This is important for bookkeeping accuracy and
              compliance. This also allows us to:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Provide financial insights and recommendations</li>
                <li>Calculate accurate quarterly tax estimates</li>
                <li>
                  Check that all income and expenses are properly accounted for
                </li>
              </ul>
            </Text>
          </Accordion.Content>
        </Accordion.Item>

        <Accordion.Item value="missing-statement">
          <Accordion.Trigger>
            What if I can&apos;t find a statement right now?
          </Accordion.Trigger>
          <Accordion.Content>
            <Text>
              We <b>strongly recommend</b> taking the time to upload all
              required statements. They are needed to fully set up your account,
              as well as for compliance, tax, and bookkeeping purposes.
            </Text>
          </Accordion.Content>
        </Accordion.Item>

        <Accordion.Item value="multi-month-statement">
          <Accordion.Trigger>
            What if I have a statement that covers multiple accounts or months?
          </Accordion.Trigger>
          <Accordion.Content>
            <Text>
              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  Select{' '}
                  <b>
                    “I have a statement covering multiple months or accounts”
                  </b>
                  .
                </li>
                <li>
                  Upload the statement to the statements card that appears.
                  It&apos;s okay to upload the same statement for each covered
                  account.
                </li>
              </ol>
            </Text>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      {statements?.length > 0 && (
        <EditableStatementTable documents={statements} />
      )}

      {!checked && (
        <Button onClick={() => setUploadFileModalOpen(true)}>
          <Icon icon={faUpload} className="mr-2" />
          Upload
        </Button>
      )}

      <FileUploadModal
        documentType={UploadDocumentType.STATEMENT}
        open={uploadFileModalOpen}
        close={() => setUploadFileModalOpen(false)}
        userFacing
        useFilteredAccountOptions
      />

      <Checkbox
        checked={checked}
        onClick={handleSkip}
        label="I just created my business bank account and don't have any statements yet."
      />

      {checked && (
        <Alert
          className="bg-neutral-25 p-4"
          variant="default"
          description="No problem. We'll ask you to upload your statements later."
        />
      )}
    </div>
  )
}
